// src/features/reporting/pages/inventory/AbcAnalysisPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Chip, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, Typography, useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Area, AreaChart, CartesianGrid, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import PageHeader from '../../../../components/common/PageHeader';
import { useApi } from '../../../../hooks/api/useApi';
import { formatDKK } from '../../../../utils/formatters/currencyFormatter';
import { usePermissions } from '../../../auth/hooks/usePermissions';
import { useProductGroups } from '../../../inventory/hooks/useProductGroups';
import { inventoryAnalyticsAPI } from '../../services/inventoryAnalytics';
import InfoTip, { withInfo } from '../../../../components/common/InfoTip';
import {
  ChartCard, FilterRow, PeriodSelect, SERIES_COLORS, StatRow, StatTile, formatPercent, periodDates,
} from '../../components/analysisParts';

// A/B/C are ordered, so one hue light → dark (Orbit primary 600 / 300 / 100), not three hues
const CLASS_STYLE = {
  A: { bgcolor: '#1F4B8E', color: '#FFFFFF' },
  B: { bgcolor: '#9AB2D6', color: '#12284D' },
  C: { bgcolor: '#E4EBF5', color: '#12284D' },
  none: { bgcolor: 'transparent', color: 'text.secondary', border: '1px solid', borderColor: 'divider' },
};

export function AbcChip({ value }) {
  const { t } = useTranslation(['reporting']);
  return (
    <Chip
      size="small"
      label={value === 'none' ? t('reporting:abc.class_none_short') : value}
      sx={{ ...CLASS_STYLE[value], fontWeight: 700, minWidth: 32 }}
    />
  );
}

export default function AbcAnalysisPage() {
  const { t } = useTranslation(['reporting', 'common']);
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasScope } = usePermissions();
  const canFilterGroups = hasScope('inventory:product-group:read');
  const canOpenItems = hasScope('inventory:item:read');
  const { productGroups, fetchProductGroups } = useProductGroups();
  const { loading, error, execute, clearError } = useApi();

  const [days, setDays] = useState(365);
  const [basis, setBasis] = useState('value');
  const [productGroupId, setProductGroupId] = useState('');
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    if (canFilterGroups) fetchProductGroups();
  }, [canFilterGroups, fetchProductGroups]);

  const load = useCallback(async () => {
    const result = await execute(() => inventoryAnalyticsAPI.getAbcAnalysis({
      ...periodDates(days), basis, productGroupId,
    }));
    if (result.success) setAnalysis(result.data.data);
  }, [execute, days, basis, productGroupId]);

  useEffect(() => { load(); }, [load]);

  const byValue = basis === 'value';
  const formatAmount = (amount) => (byValue ? formatDKK(amount, { whole: true }) : amount.toLocaleString('da-DK'));

  // The Pareto curve: cumulative share against item rank, moving items only
  const curve = useMemo(() => (analysis?.items ?? [])
    .filter((item) => item.abc_class !== 'none')
    .map((item, index) => ({ rank: index + 1, ...item })), [analysis]);
  const lastRankOf = (abcClass) => curve.filter((item) => item.abc_class === abcClass).at(-1)?.rank;
  const classes = Object.fromEntries((analysis?.classes ?? []).map((c) => [c.abc_class, c]));

  const columns = [
    withInfo({
      field: 'abc_class',
      headerName: t('reporting:abc.columns.class'),
      width: 110,
      renderCell: ({ value }) => <AbcChip value={value} />,
    }, t('reporting:help.abc.class')),
    withInfo({
      field: 'xyz_class',
      headerName: 'XYZ',
      width: 100,
      renderCell: ({ value, row }) => (value
        ? (
          <MuiTooltip title={t(`reporting:abc.xyz.${value}`, { cv: row.variation?.toLocaleString('da-DK') })}>
            <Chip size="small" variant="outlined" label={value} sx={{ minWidth: 32 }} />
          </MuiTooltip>
        )
        : <Typography variant="body2" color="text.secondary">—</Typography>),
    }, t('reporting:help.abc.xyz')),
    {
      field: 'name',
      headerName: t('reporting:abc.columns.item'),
      flex: 1.6,
      minWidth: 180,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.sku}</Typography>
        </Box>
      ),
    },
    {
      field: 'product_group_name',
      headerName: t('reporting:analysis.product_group'),
      flex: 1,
      minWidth: 140,
      renderCell: ({ value }) => <Typography variant="body2" noWrap title={value}>{value}</Typography>,
    },
    withInfo({
      field: 'consumption_quantity',
      headerName: t('reporting:abc.columns.quantity'),
      flex: 0.8,
      minWidth: 120,
      type: 'number',
      valueFormatter: (value, row) => `${value.toLocaleString('da-DK')} ${row.uom}`,
    }, t('reporting:help.abc.quantity')),
    withInfo({
      field: 'consumption_value',
      headerName: t('reporting:abc.columns.value'),
      flex: 1,
      minWidth: 130,
      type: 'number',
      // Dispensed, but before valuation went live or without a price
      renderCell: ({ value, row }) => (row.consumption_quantity > 0 && value === 0
        ? <Typography variant="body2" color="text.secondary" fontStyle="italic">{t('reporting:abc.unknown_cost')}</Typography>
        : formatDKK(value, { whole: true })),
    }, t('reporting:help.abc.value')),
    // Unclassified items are not part of the ranking, so no share
    withInfo({
      field: 'share',
      headerName: t('reporting:abc.columns.share'),
      flex: 0.7,
      type: 'number',
      valueFormatter: (value, row) => (row.abc_class === 'none' ? '—' : formatPercent(value)),
    }, t('reporting:help.abc.share')),
    withInfo({
      field: 'cumulative_share',
      headerName: t('reporting:abc.columns.cumulative'),
      flex: 0.8,
      type: 'number',
      valueFormatter: (value, row) => (row.abc_class === 'none' ? '—' : formatPercent(value)),
    }, t('reporting:help.abc.cumulative')),
  ];

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <PageHeader
        onBack={() => navigate('/reports')}
        title={t('reporting:abc.title')}
        subtitle={t('reporting:abc.subtitle')}
      />

      <FilterRow>
        <PeriodSelect value={days} onChange={setDays} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup size="small" exclusive value={basis} onChange={(_, next) => next && setBasis(next)}>
            <ToggleButton value="value">{t('reporting:abc.basis.value')}</ToggleButton>
            <ToggleButton value="quantity">{t('reporting:abc.basis.quantity')}</ToggleButton>
          </ToggleButtonGroup>
          <InfoTip title={t('reporting:help.abc.basis')} />
        </Box>
        {canFilterGroups && (
          <TextField
            select
            size="small"
            label={t('reporting:analysis.product_group')}
            value={productGroupId}
            onChange={(e) => setProductGroupId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">{t('reporting:analysis.all_groups')}</MenuItem>
            {productGroups.map((group) => (
              <MenuItem key={group.product_group_id} value={group.product_group_id}>{group.name}</MenuItem>
            ))}
          </TextField>
        )}
      </FilterRow>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>{error}</Alert>}

      <StatRow>
        {['A', 'B', 'C', 'none'].map((abcClass) => (
          <StatTile
            key={abcClass}
            loading={loading && !analysis}
            label={t(`reporting:abc.class_label.${abcClass}`)}
            value={t('reporting:abc.items_count', { count: classes[abcClass]?.item_count ?? 0 })}
            caption={abcClass === 'none'
              ? t('reporting:abc.class_caption.none')
              : t('reporting:abc.class_caption.share', { share: formatPercent(classes[abcClass]?.share ?? 0) })}
            info={t(`reporting:help.abc.class_${abcClass.toLowerCase()}`)}
          />
        ))}
      </StatRow>

      <ChartCard
        title={t('reporting:abc.chart_title')}
        info={t('reporting:help.abc.chart')}
        description={analysis
          ? t(byValue ? 'reporting:abc.chart_description_value' : 'reporting:abc.chart_description_quantity', { total: formatAmount(analysis.total) })
          : null}
      >
        {!loading && curve.length === 0 && (
          <Typography color="text.secondary">{t('reporting:abc.empty')}</Typography>
        )}
        {curve.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={curve} margin={{ top: 16, right: 16, left: 0, bottom: 16 }}>
              <CartesianGrid stroke={theme.palette.divider} vertical={false} />
              {lastRankOf('A') && <ReferenceArea x1={1} x2={lastRankOf('A')} fill="#1F4B8E" fillOpacity={0.08} label={{ value: 'A', position: 'insideTop', fill: theme.palette.text.secondary }} />}
              {lastRankOf('B') && <ReferenceArea x1={lastRankOf('A') ?? 1} x2={lastRankOf('B')} fill="#9AB2D6" fillOpacity={0.12} label={{ value: 'B', position: 'insideTop', fill: theme.palette.text.secondary }} />}
              {lastRankOf('C') && <ReferenceArea x1={lastRankOf('B') ?? 1} x2={lastRankOf('C')} fill="#E4EBF5" fillOpacity={0.2} label={{ value: 'C', position: 'insideTop', fill: theme.palette.text.secondary }} />}
              <XAxis
                dataKey="rank"
                type="number"
                domain={[1, curve.length]}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
                label={{ value: t('reporting:abc.axis_items'), position: 'insideBottomRight', offset: -14, fill: theme.palette.text.secondary, fontSize: 12 }}
                allowDecimals={false}
              />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(value) => `${value} %`}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
                width={56}
              />
              <ReferenceLine y={analysis.a_threshold} stroke={theme.palette.text.disabled} strokeDasharray="4 4" />
              <ReferenceLine y={analysis.b_threshold} stroke={theme.palette.text.disabled} strokeDasharray="4 4" />
              <Tooltip
                formatter={(value) => [formatPercent(value), t('reporting:abc.columns.cumulative')]}
                labelFormatter={(rank) => {
                  const item = curve[rank - 1];
                  return item ? `${rank}. ${item.name} (${item.abc_class})` : rank;
                }}
                contentStyle={{
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
                itemStyle={{ color: theme.palette.text.primary }}
              />
              <Area
                type="stepAfter"
                dataKey="cumulative_share"
                stroke={SERIES_COLORS[0]}
                strokeWidth={2}
                fill={SERIES_COLORS[0]}
                fillOpacity={0.12}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title={t('reporting:abc.table_title')} description={t('reporting:abc.table_description')}>
        <DataGrid
          rows={analysis?.items ?? []}
          getRowId={(row) => row.item_id}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={canOpenItems ? ({ row }) => navigate(`/inventory/items/${row.item_id}`) : undefined}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-row': { cursor: canOpenItems ? 'pointer' : 'default' },
            '& .MuiDataGrid-cell': { alignItems: 'center', fontVariantNumeric: 'tabular-nums' },
          }}
        />
      </ChartCard>
    </Box>
  );
}
